import React from 'react';
import { InputAdornment, Switch, TextField } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import CustomButton from '../../../components/buttons/CustomButton';
import PopupBase from '../../../popup/base/PopupBase';
import { formatBRL, sessionsLabel, totalSessions } from '../packageFormat';
import ProcedurePicker from './ProcedurePicker';
import PriceBlock from './PriceBlock';
import styles from './PackageFormPopup.module.css';

/** Bloco do formulário: subtítulo com número, um complemento opcional à direita e o conteúdo. */
function Block({ step, title, aside, children }) {
  return (
    <section className={styles.block}>
      <header className={styles.blockHeader}>
        <span className={styles.step}>{step}</span>
        <h4 className={styles.blockTitle}>{title}</h4>
        {aside && <span className={styles.blockAside}>{aside}</span>}
      </header>
      {children}
    </section>
  );
}

/** Linha com ícone, título, uma linha de ajuda e o interruptor. */
function ToggleRow({ Icon, title, hint, checked, onChange, label }) {
  return (
    <label className={styles.toggle}>
      <span className={styles.toggleIcon}><Icon fontSize="small" /></span>
      <span className={styles.toggleText}>
        <span className={styles.toggleTitle}>{title}</span>
        <span className={styles.toggleHint}>{hint}</span>
      </span>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} inputProps={{ 'aria-label': label }} />
    </label>
  );
}

/**
 * Criar ou editar pacote em quatro blocos: identificação, sessões, validade e preço.
 * Todo o estado vem de usePackageForm (form); salvar chama onSave da página.
 */
/** "o nome, a validade e o preço": junta o que falta com vírgulas e "e" no fim. */
const joinMissing = (items) => (items.length > 1
  ? `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`
  : items[0] || '');

function PackageFormPopup({ form, procedures, saving, error, onClose, onSave }) {
  const {
    formData, editingPackage, semValidade, validityError, totalValue, canSave, missing,
    setField, setActive, setNoExpiry, setValidity, validateValidityOnBlur, setProcedureQuantity,
  } = form;
  const sessions = totalSessions(formData.procedures);

  const content = (
    <div className={styles.content}>
      <div className={styles.hero}>
        <span className={styles.heroIcon}><Inventory2OutlinedIcon /></span>
        <span className={styles.heroText}>
          <span className={styles.heroName}>{formData.name || 'Pacote sem nome'}</span>
          <span className={styles.heroMeta}>
            {sessions > 0 ? `${sessionsLabel(sessions)} · ${formatBRL(totalValue)} avulso` : 'Nenhuma sessão escolhida'}
          </span>
        </span>
        <span className={`${styles.heroStatus} ${formData.active ? styles.on : styles.off}`}>
          {formData.active ? 'Ativo' : 'Inativo'}
        </span>
      </div>

      <Block step={1} title="Identificação">
        <label className={styles.field}>
          <span className={styles.label}>Nome do pacote *</span>
          <TextField
            size="small"
            name="name"
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="Ex.: 10 sessões de fisioterapia"
            className={styles.input}
            inputProps={{ 'aria-label': 'Nome do pacote' }}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Descrição</span>
          <TextField
            size="small"
            name="description"
            value={formData.description}
            onChange={(e) => setField('description', e.target.value)}
            multiline
            minRows={2}
            placeholder="O que este pacote oferece, em poucas palavras"
            className={styles.input}
            inputProps={{ 'aria-label': 'Descrição' }}
          />
        </label>
        <ToggleRow
          Icon={ToggleOnOutlinedIcon}
          title={formData.active ? 'Pacote ativo' : 'Pacote inativo'}
          hint="Desligado, fica guardado e não aparece para vender."
          checked={!!formData.active}
          onChange={setActive}
          label="Pacote ativo"
        />
      </Block>

      <Block step={2} title="Sessões incluídas" aside={sessions > 0 ? sessionsLabel(sessions) : null}>
        <ProcedurePicker procedures={procedures} selected={formData.procedures} onChange={setProcedureQuantity} />
      </Block>

      <Block step={3} title="Validade">
        <div className={styles.validityRow}>
          <label className={styles.field}>
            <span className={styles.label}>Vale por</span>
            <TextField
              size="small"
              name="validity_days"
              value={semValidade ? '' : formData.validity_days}
              onChange={(e) => setValidity(e.target.value)}
              onBlur={validateValidityOnBlur}
              onWheel={(e) => e.target.blur()}
              type="number"
              placeholder={semValidade ? '' : 'Ex.: 90'}
              required
              disabled={semValidade}
              error={!!validityError}
              helperText={validityError || 'Contados a partir da venda.'}
              className={styles.input}
              InputProps={{
                endAdornment: <InputAdornment position="end">dias</InputAdornment>,
                inputProps: { min: 0, 'aria-label': 'Validade em dias' },
              }}
            />
          </label>
          <ToggleRow
            Icon={AllInclusiveRoundedIcon}
            title="Não expira"
            hint="As sessões ficam valendo sem prazo."
            checked={semValidade}
            onChange={setNoExpiry}
            label="Não expira"
          />
        </div>
      </Block>

      <Block step={4} title="Preço">
        <PriceBlock form={form} />
      </Block>
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <span className={`${styles.footerHint} ${error ? styles.footerError : ''}`} role={error ? 'alert' : undefined}>
        {error || (canSave ? '' : `Falta informar ${joinMissing(missing)}.`)}
      </span>
      <CustomButton label="Cancelar" variant="text" style="primary" onClick={onClose} />
      <CustomButton
        label="Salvar pacote"
        variant="contained"
        style="main"
        onClick={onSave}
        loading={saving}
        enabled={canSave && !saving}
      />
    </div>
  );

  return (
    <PopupBase
      Title={editingPackage ? 'Editar pacote' : 'Novo pacote'}
      Content={content}
      Footer={footer}
      OnClose={onClose}
      maxWidth="md"
    />
  );
}

export default PackageFormPopup;
